// deriveSubqueryEditContext — produces the child VqeEditContext for a DRILLED
// subquery body (WHERE IN(SELECT), scalar subquery, FROM derived table, JOIN
// LATERAL). The drilled <SelectRenderer> uses this so its edit affordances route
// changes into the subquery's byte range instead of the outer statement.
//
// This DELIBERATELY mirrors `deriveBranchEditContext` (set-op branches) rather
// than refactoring a shared primitive: keeping the set-op path untouched gives
// this slice zero blast radius there. Both helpers:
//   - synthesise a per-body StatementInfo (via makeNestedSubqueryStatement) so
//     resolveEdit routes through applyStructuralEditForStatement (range-based),
//     not the id-based applyStructuralEdit path
//   - re-resolve the Resolver scope at the body start offset
//   - spread document-scoped parent fields (service, documentId, model,
//     editor, getLiveSource) — no new service or model
//
// Pure, no side effects, no class or enum.

import { makeNestedSubqueryStatement } from '../render/select/nestedStatement';
import type { VqeEditContext } from './editContext';

/**
 * Derives the child `VqeEditContext` for a drilled subquery body.
 *
 * @param parent            The tab-level (outer) VqeEditContext.
 * @param parentStatementId The REAL (non-synthetic) id of the statement that
 *                          CONTAINS the subquery — from
 *                          `documentStructure.statements`. The synthetic body id
 *                          is keyed off this; passing an already-synthetic id
 *                          would double-nest it.
 * @param bodyStart         Absolute byte offset of the first body char (just
 *                          inside the subquery's opening paren).
 * @param bodyEnd           Absolute byte offset one past the last body char
 *                          (the matching close paren's offset, trailing
 *                          whitespace trimmed).
 */
export function deriveSubqueryEditContext(
  parent: VqeEditContext,
  parentStatementId: string,
  bodyStart: number,
  bodyEnd: number,
): VqeEditContext {
  const statementInfo = makeNestedSubqueryStatement(parentStatementId, bodyStart, bodyEnd);
  const scope = parent.service.resolveScope(parent.documentId, {
    offset: bodyStart,
    line: 0,
    column: 0,
  });
  return {
    ...parent,
    statementId: statementInfo.id,
    statementInfo,
    scope,
  };
}
